import { ApolloClient } from '@apollo/client'
import { getSignedUrls, getSignedUrlsVariables } from '__generatedTypes__/getSignedUrls'
import { FileProcessingStatus } from '__generatedTypes__/globalTypes'
import { PlatformSlug } from 'constants/consts'
import { GET_SIGNED_URL } from 'queries/common'
import { buildCdnUrl, getFileExtension } from './assetUrls'
import { REAL_STUDENT_REVIEWS_S3_PATH } from './consts'
import { RealStudentReview, RealStudentReviewAssetRefs, UploadedReviewAsset, UploadedReviewAssets } from './types'

type BackendVideoReview = {
  id: string
  createdAt: string
  updatedAt: string
  video: string
  thumbnail: string
  avatar: string
  fullName: string
  description: string
  order: number
  isLive: boolean
  videoProcessingStatus?: FileProcessingStatus | null
  videoProcessingError?: string | null
}

type UploadAssetParams = {
  apolloClient: ApolloClient<object>
  path: string
  file: File
  fileName: string
  fallbackContentType: string
}

const getReviewAssetBasePath = (platformSlug: PlatformSlug, reviewId: string) =>
  `${REAL_STUDENT_REVIEWS_S3_PATH}/${platformSlug}/${reviewId}`

const getContentType = (file: File, fallbackContentType: string) => file.type || fallbackContentType

const createAssetPath = (path: string, fileName: string) => `${path}/${fileName}`

const uploadAsset = async ({
  apolloClient,
  path,
  file,
  fileName,
  fallbackContentType,
}: UploadAssetParams): Promise<UploadedReviewAsset> => {
  const contentType = getContentType(file, fallbackContentType)
  const { data } = await apolloClient.query<getSignedUrls, getSignedUrlsVariables>({
    query: GET_SIGNED_URL,
    variables: {
      query: {
        contentType,
        fileName,
        method: 'putObject',
        path,
      },
    },
    fetchPolicy: 'network-only',
  })

  const response = await fetch(data.getSignedUrls, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'Content-Disposition': `attachment; filename*=UTF-8''${encodeURIComponent(fileName)}`,
    },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Failed to upload ${fileName}`)
  }

  const assetPath = createAssetPath(path, fileName)

  return {
    path: assetPath,
    url: buildCdnUrl(assetPath),
    previewUrl: buildCdnUrl(assetPath),
    fileName,
    contentType,
    size: file.size,
  }
}

export const hydrateVideoReview = (review: BackendVideoReview, platformSlug: PlatformSlug): RealStudentReview => ({
  ...review,
  platformSlug,
  videoUrl: review.video ? buildCdnUrl(review.video) : '',
  thumbnailUrl: review.thumbnail ? buildCdnUrl(review.thumbnail) : '',
  avatarUrl: review.avatar ? buildCdnUrl(review.avatar) : '',
})

export const isVideoReviewProcessing = (status?: FileProcessingStatus | null): boolean =>
  status === FileProcessingStatus.PENDING || status === FileProcessingStatus.PROCESSING

export const formatVideoProcessingStatus = (status?: FileProcessingStatus | null) => {
  if (!status) {
    return '—'
  }

  switch (status) {
    case FileProcessingStatus.PENDING:
      return 'Pending'
    case FileProcessingStatus.PROCESSING:
      return 'Processing'
    case FileProcessingStatus.COMPLETED:
      return 'Completed'
    case FileProcessingStatus.FAILED:
      return 'Failed'
    default:
      return status
  }
}

export const isReviewPublishable = (review: RealStudentReview) => {
  const isVideoReady = !review.videoProcessingStatus || review.videoProcessingStatus === FileProcessingStatus.COMPLETED

  return Boolean(
    isVideoReady &&
      review.video &&
      review.thumbnail &&
      review.avatar &&
      review.fullName.trim() &&
      review.description.trim()
  )
}

export const uploadReviewAssets = async ({
  apolloClient,
  platformSlug,
  reviewId,
  videoFile,
  thumbnailFile,
  avatarFile,
}: {
  apolloClient: ApolloClient<object>
  platformSlug: PlatformSlug
  reviewId: string
  videoFile?: File
  thumbnailFile?: File
  avatarFile?: File
}): Promise<UploadedReviewAssets> => {
  const basePath = getReviewAssetBasePath(platformSlug, reviewId)
  const assetVersion = Date.now()
  const assetRefs: RealStudentReviewAssetRefs = {}
  let videoFileName: string | undefined

  if (videoFile) {
    const extension = getFileExtension(videoFile.name, 'mp4')
    videoFileName = `video-${assetVersion}.${extension}`
    const videoAsset = await uploadAsset({
      apolloClient,
      path: basePath,
      file: videoFile,
      fileName: videoFileName,
      fallbackContentType: 'video/mp4',
    })

    assetRefs.video = videoAsset.path
    assetRefs.videoUrl = videoAsset.previewUrl
  }

  if (thumbnailFile) {
    const extension = getFileExtension(thumbnailFile.name, 'jpg')
    const thumbnailAsset = await uploadAsset({
      apolloClient,
      path: basePath,
      file: thumbnailFile,
      fileName: `thumbnail-${assetVersion}.${extension}`,
      fallbackContentType: 'image/jpeg',
    })

    assetRefs.thumbnail = thumbnailAsset.path
    assetRefs.thumbnailUrl = thumbnailAsset.previewUrl
  }

  if (avatarFile) {
    const extension = getFileExtension(avatarFile.name, 'jpg')
    const avatarAsset = await uploadAsset({
      apolloClient,
      path: basePath,
      file: avatarFile,
      fileName: `avatar-${assetVersion}.${extension}`,
      fallbackContentType: 'image/jpeg',
    })

    assetRefs.avatar = avatarAsset.path
    assetRefs.avatarUrl = avatarAsset.previewUrl
  }

  return {
    refs: assetRefs,
    videoFileName,
  }
}
